import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Row from '../layout/Row';
import Col from '../layout/Col';
import { between, generateId, getPortalTarget } from '../utility/functions';
import { SCREEN_WIDTH } from '../../constants/config';
import { createPortal } from 'react-dom';
import RoleProtected from '../RoleProtected';
import Icon from '../forms/Icon';
import {ContentScrollContext} from '../layout/Content.js';

const Cell = styled.td`
    all: unset;
    position: relative;
    overflow: hidden;
    padding: unset !important;
`
const ElipsisMenu = styled.div`
    background: ${({ theme }) => theme.background || "#fff"};
    box-shadow: 0px 3px 10px ${({ theme }) => theme.shadow || "rgba(0,0,0,0.2)"};
    border: 1px solid ${({ theme }) => `${theme.gray}40`};
    border-radius: 0.5em;    
    display: grid;
    gap: 0.2em;
    overflow: hidden;
    padding: 0.5em;
    position: fixed;  
    transition: all 0.2s;      
    width: auto;  
    white-space: nowrap;
    z-index: 1000;
`
const ElipsisMenuItem = styled.button`
    border: unset;
    font-size: 0.85em;
    padding: 0.5em 1em;
    transition: all 0.2s;
    border-radius: 0.5rem;
    background-color: unset;
    cursor: pointer;
    text-align: left;
    color: ${({ theme, color }) => theme[color] || theme.text};

    &:hover {
        background-color: ${({ theme }) => theme.button.hover}80;
    }

    &:focus {
        outline: none !important;
        border: unset;
    }
`

const ElipsisMenuItemsEmpty = styled.div`
    color: ${({ theme }) => `${theme.gray}80`};   
    font-size: 0.75em;   
    padding: 0.5em 1em;
    font-style: italic;
`

const ElipsisMenuItemIconWrapper = styled.div`
    display: inline-block;
    margin-right: 14px;
`

const ElipsisButton = styled.button`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: 1px solid ${({ theme }) => `${theme.gray}40`};
    border-radius: 0.5em;
    user-select: none;
    color: ${({ theme, color }) => theme[color] || theme.text};
    transition: background-color, transform, 0.3s;
    background-color: ${({ theme }) => theme.button.background};
    font-weight: bold;
    font-size: inherit;
    padding: 0.2em 0.5em;
    overflow: hidden;
    min-height: 1.5em;
    margin-right: 10px;

    &:hover {
        background-color: ${({ theme }) => theme.button.hover};
        border-color: ${({ theme }) => theme.button.hover};
        color: ${({ theme }) => theme.button.hoverText};
        // transform: translateY(-1px);
        // box-shadow: 0px 2px 2px ${({ theme }) => theme.shadow};
    }
    &:active {
        background-color: ${({ theme }) => theme.button.active};
        color: ${({ theme }) => theme.button.activeText};
        box-shadow: unset;
    }
    &:disabled {
        color: ${({ theme }) => theme.button.disabledText};
        background-color: ${({ theme }) => theme.button.disabled};
        border-color: unset;
        transform: unset;
        box-shadow: unset;
        cursor: default;
    }
    &:focus {
        outline: none !important;
        border: 1px solid ${({ theme }) => theme.text};
        // box-shadow: 0 0 3px ${({ theme }) => theme.highlight};
    }
`;

const Expander = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5em;
    position: absolute;
    top: -1.2em;
    margin: 0 1em 0 0;
`

const ManagementRow = ({ controls, children, ...props }) => {
    const rowRef = useRef(null)
    
    const EllipsisMenu = () => {       
        const scrollRef = useContext(ContentScrollContext);
        const elipsisButtonRef = useRef(null);
        const menuRef = useRef(null);
        const [menuOpen, setMenuOpen] = useState(false);

        const portalTarget = getPortalTarget();
        
        const toggleMenu = () => setMenuOpen(prev => !prev);

        const handleMenuClose = (event) => {
            const button = elipsisButtonRef.current;       
            if (menuRef.current && (!event || !menuRef.current.contains(event.target))) {
                setMenuOpen(false);
                button.disabled = false;
            }
        };

        useEffect(() => {
            if (menuOpen) {
                const viewportHeight = window.innerHeight;
                const viewportWidth = window.innerWidth;
                
                const button = elipsisButtonRef.current;
                const buttonRect = button.getBoundingClientRect();
                
                const menu = menuRef.current;
                const menuHeight = menu.offsetHeight;
                const menuWidth = menu.offsetWidth;

                let top = buttonRect.top + buttonRect.height + 2;
                let left = buttonRect.left;

                if (top + menuHeight + 36 > viewportHeight)
                    top = Math.max(viewportHeight - menuHeight - 36, 36);

                if (left + menuWidth + 36 > viewportWidth - 36)
                    left = Math.max(viewportWidth - menuWidth - 36, 36);
                
                button.disabled = true;
                menu.style.top = `${top}px`;
                menu.style.left = `${left}px`;
            }
        }, [menuOpen]);

        useEffect(() => {            
            document.addEventListener('mousedown', handleMenuClose);
            return () => document.removeEventListener('mousedown', handleMenuClose);
        }, []);

        useEffect(() => {
            const scrollable = scrollRef?.current;
            if (!scrollable) return;
            scrollable.addEventListener('scroll', handleMenuClose);
            return () => scrollable.removeEventListener('scroll', handleMenuClose);
        }, []);

        return (
            <tr ref={rowRef}>
                {children}
                <Cell>
                    {controls?.length > 0 ? (
                        <>
                            <ElipsisButton ref={elipsisButtonRef} onClick={toggleMenu}>
                                <Icon icon='elipsis' />
                            </ElipsisButton>

                            {menuOpen && createPortal(
                                <ElipsisMenu ref={menuRef}>
                                    {controls.map((control, index) => (
                                        <RoleProtected roles={control.props.roles ?? null} key={generateId()}>
                                            <ElipsisMenuItem
                                                key={control.key ?? index}
                                                onClick={(e) => {
                                                    handleMenuClose();
                                                    control.props.onClick?.(e);
                                                }}
                                            >
                                                {control.props.icon && (
                                                    <ElipsisMenuItemIconWrapper>
                                                        <Icon icon={control.props.icon} />
                                                    </ElipsisMenuItemIconWrapper>
                                                )}
                                                {control.props.label ?? control.props.children}
                                            </ElipsisMenuItem>
                                        </RoleProtected>
                                    ))}
                                </ElipsisMenu>,
                                portalTarget
                            )}
                        </>
                    ) : (
                        <>
                            {props.showMenuWithNoControls && (
                                <>
                                    <ElipsisButton ref={elipsisButtonRef} onClick={toggleMenu}>
                                        <Icon icon='elipsis' />
                                    </ElipsisButton>

                                    {menuOpen && createPortal(
                                        <ElipsisMenu ref={menuRef}>
                                            <ElipsisMenuItemsEmpty>No Actions Available</ElipsisMenuItemsEmpty>
                                        </ElipsisMenu>,
                                        portalTarget
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Cell>
            </tr>
        );
    };
  

    const SlideInMenu = () => {
        const mRef = useRef(null)
        const enabled = true;
        const [show, setShow] = useState(false);
        const [mousePos, setMousePos] = useState(null);
        const [leftOf, setLeftOf] = useState(20);
        const [transition, setTransition] = useState(false);

        const handleMouseMove = (e) => {
            const boundingRect = rowRef.current.getBoundingClientRect();
            const mouseX = (e.clientX - boundingRect.left) / (boundingRect.width * 0.69);
            setMousePos(mouseX);

            if (!mRef.current) return
            const mBoundingRect = mRef.current.getBoundingClientRect();
            setLeftOf(show ? between((mousePos * mBoundingRect.width) * 1.1, 0, mBoundingRect.width + 30) * -1 : 20)
        }

        useEffect(() => {
            if (transition) {
                setTimeout(() => setTransition(false), 150)
            }
        }, [transition])        
        
        return (
            <tr
                ref={rowRef}
                onMouseEnter={() => {
                    setTransition(true)
                    setShow(true)
                }}
                onMouseLeave={() => {
                    setTransition(true)
                    setShow(false)
                    setMousePos(null)
                    setLeftOf(20)
                }}
                onMouseMove={handleMouseMove}
                {...props}
            >
                {children}
                <Cell>
                    {SCREEN_WIDTH ? <Row>{controls}</Row> : enabled ? (
                        <Expander
                            ref={mRef}
                            style={{
                                left: `${leftOf}px`,
                                transition: transition ? 'all 0.15s' : '',
                            }}
                        >
                            {controls}
                        </Expander>
                    ) : <Col>{controls}</Col>}
                </Cell>
            </tr> 
        )
    };

    switch (props.layout)
    {
        case 'elipsis':
            return (               
                <EllipsisMenu />        
            );
        default:
            return (
                <SlideInMenu />
            );
    }
}

export default ManagementRow;
